import * as React from 'react';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';

import { NewBreadcrumb } from '../../../components/synced/ui/NewBreadcrumb/NewBreadcrumb';
import { alertWarn } from '../../../store/reducers/Alert';
import { services } from '../../../services';
import { useBackgroundPromiseWrapper } from '../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useDownload } from '../../../helpers/hooks/useDownload';
import { useIsMobile } from '../../../helpers/hooks/useIsMobile';
import { GreenTableCard } from '../../reports/components/GreenTableCard';
import { ReportFilterBar } from '../../reports/components/ReportFilterBar';
import { ReportPageHeader } from '../../reports/components/ReportPageHeader';
import { useReportFilters } from '../../reports/components/useReportFilters';
import { STATUS_TITLE } from '../../reports/components/statusTitles';
import styles from './ReferenceReport.module.css';

const CHART_ORDER = [
    { title: STATUS_TITLE.TOTAL,    color: '#aeaeb2' },
    { title: STATUS_TITLE.NOT_SUBM, color: '#e0342b' },
    { title: STATUS_TITLE.SUBM,     color: '#f5d90a' },
    { title: STATUS_TITLE.CHECKING, color: '#0a84ff' },
    { title: STATUS_TITLE.IN_WORK,  color: '#f5a623' },
    { title: STATUS_TITLE.APPROVED, color: '#8bc34a' },
    { title: STATUS_TITLE.GIP,      color: '#b0b4b8' },
    { title: STATUS_TITLE.REVOKED,  color: '#e0342b' },
    { title: STATUS_TITLE.APPR_REM, color: '#5aa9e6' },
];

interface StatusItem {
    Title: string;
    Value: number;
}

interface TableValue {
    Title: string;
    Value?: string | number | null;
    ValueFact?: string | number | null;
}

interface Table4Value {
    ContractorName: string;
    Position: string;
    Title: string;
    Email: string;
    PhoneNumber: string;
}

export const ReferenceReport: React.FC = () => {
    const dispatch = useDispatch();
    const filters = useReportFilters();
    const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper();
    const { downloadFile, isDownloading } = useDownload();
    const [data, setData] = React.useState<any>();
    const isMobile = useIsMobile(860);

    React.useEffect(() => {
        document.body.style.background = 'var(--bg-page)';
        return () => {
            document.body.style.background = '';
        };
    }, []);

    const breadCrumbItems = [
        { label: 'Отчёты', to: '/reports' },
        { label: 'Справка' }
    ];

    const viewReport = () => {
        if (!filters.selectedProjectDoc) {
            dispatch(alertWarn({ detail: 'Необходимо выбрать документацию' }));
            return;
        }
        promiseWrapper(
            () => services.projectDocService.referenceReport(filters.selectedProjectDoc!.Id ?? 0),
            (res: any) => setData(res)
        );
    };

    const downloadReport = () => {
        const id = filters.selectedProjectDoc?.Id;
        if (id) void downloadFile(`ProjectDocs/ReferenceReportDownload?id=${id}`);
    };

    const statusMap = Object.fromEntries(
        (data?.Statuses as StatusItem[] ?? []).map((s: StatusItem) => [s.Title, s.Value])
    );
    const maxChartVal = Math.max(...CHART_ORDER.map(c => statusMap[c.title] ?? 0), 1);
    const chartRows = CHART_ORDER.map(c => ({
        label: c.title,
        value: statusMap[c.title] ?? 0,
        width: Math.round(((statusMap[c.title] ?? 0) / maxChartVal) * 100),
        barColor: c.color
    }));

    return (
        <div className={clsx(styles.page, 'reference-report-page')}>
            <NewBreadcrumb items={breadCrumbItems} grayAncestors />

            <ReportFilterBar
                {...filters}
                isLoading={isLoading}
                isDownloading={isDownloading}
                onView={viewReport}
                onDownload={downloadReport}
            />

            {data && (
                <div className={styles.report}>
                    <ReportPageHeader title={data.Name} date={data.Date} />

                    {/*{data.GIP && <p className={styles.gip}>{data.GIP}</p>}*/}

                    <div className={styles.mainGrid}>
                        <GreenTableCard title={data.Table1?.Header ?? 'Основные реперные точки проекта'}>
                            {isMobile ? (
                                <div className={styles.mobileCards}>
                                    {(data.Table1?.Values as TableValue[])?.map((row, i) => (
                                        <div key={i} className={styles.mobileCard}>
                                            <div className={styles.mobileCardTitle}>{row.Title}</div>
                                            {(row.Value || row.ValueFact) && (
                                                <div className={styles.mobileCardRow}>
                                                    <span className={styles.mobileCardLabel}>По договору</span>
                                                    <span>{row.Value || '—'}</span>
                                                </div>
                                            )}
                                            {row.ValueFact && (
                                                <div className={styles.mobileCardRow}>
                                                    <span className={styles.mobileCardLabel}>Выполнено</span>
                                                    <span>{row.ValueFact}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <table className={styles.table}>
                                    <thead>
                                    <tr>
                                        {(data.Table1?.Columns as string[])?.map((col, i) => (
                                            <th key={i} className={styles.th}>{col}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(data.Table1?.Values as TableValue[])?.map((row, i) => (
                                        <tr key={i} className={styles.tr}>
                                            <td className={styles.td}>{row.Title}</td>
                                            <td className={`${styles.td} ${styles.tdNum}`}>{row.Value || '—'}</td>
                                            <td className={`${styles.td} ${styles.tdNum}`}>{row.ValueFact || '—'}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}
                        </GreenTableCard>

                        <div className={styles.statusCard}>
                            <div className={styles.statusCardTitle}>Статус по документации</div>
                            <div className={styles.barChart}>
                                {chartRows.map((row, i) => (
                                    <div key={i} className={styles.barChartRow}>
                                        <span className={styles.barLabel}>{row.label}</span>
                                        <div className={styles.barTrack}>
                                            {row.value > 0 && (
                                                <div
                                                    className={styles.barFill}
                                                    style={{ width: `${row.width}%`, background: row.barColor }}
                                                />
                                            )}
                                        </div>
                                        <span className={styles.barValue}>{row.value}</span>
                                    </div>
                                ))}
                                <div className={styles.barScale}>
                                    <span />
                                    <div className={styles.barScaleTrack}>
                                        <span>0</span>
                                        <span>{Math.round(maxChartVal / 2)}</span>
                                        <span>{maxChartVal}</span>
                                    </div>
                                    <span />
                                </div>
                            </div>
                        </div>
                    </div>

                    {data.ProjectDocFolders && (
                        <div className={styles.foldersSection}>
                            <div className={styles.foldersLabel}>{data.ProjectDocFolders}</div>
                            {/*<div className={styles.folders}>{data.ProjectDocFolders}</div>*/}
                        </div>
                    )}

                    {/* Нижняя сетка: показатели + нагрузки */}
                    <div className={styles.mainGridBottom}>
                        <GreenTableCard title={data.Table2?.Header ?? 'Основные показатели объекта'}>
                            <table className={styles.table}>
                                <tbody>
                                {(data.Table2?.Values as TableValue[])?.map((row, i) => (
                                    <tr key={i} className={styles.tr}>
                                        <td className={styles.td}>{row.Title}</td>
                                        <td className={`${styles.td} ${styles.tdNum}`}>{row.Value ?? '—'}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </GreenTableCard>

                        <GreenTableCard title={data.Table3?.Header ?? 'Нагрузки'}>
                            <table className={styles.table}>
                                <thead>
                                <tr>
                                    {(data.Table3?.Columns as string[])?.map((col, i) => (
                                        <th key={i} className={styles.th}>{col}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {(data.Table3?.Values as TableValue[])?.map((row, i) => (
                                    <tr key={i} className={styles.tr}>
                                        <td className={styles.td}>{row.Title}</td>
                                        <td className={`${styles.td} ${styles.tdNum}`}>{row.Value ?? '—'}</td>
                                        <td className={`${styles.td} ${styles.tdNum}`}>{row.ValueFact ?? '—'}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </GreenTableCard>
                    </div>

                    {(data.Table4?.Values as Table4Value[])?.length > 0 && (
                        <GreenTableCard title={data.Table4?.Header ?? 'Информация о контрагентах'}
                                        className={styles.contactsCard}>
                            {isMobile ? (
                                <div className={styles.mobileCards}>
                                    {(data.Table4?.Values as Table4Value[])?.map((row, i) => (
                                        <div key={i} className={styles.contactCard}>
                                            <div className={styles.contactName}>{row.Title || '—'}</div>
                                            <div className={styles.contactMeta}>
                                                <span className={styles.contactPosition}>{row.Position}</span>
                                                {row.ContractorName && <span
                                                    className={styles.contactContractor}>{row.ContractorName}</span>}
                                            </div>
                                            {row.Email && (
                                                <a href={`mailto:${row.Email}`} className={styles.contactLink}>
                                                    <i className="fa-regular fa-envelope" /> {row.Email}
                                                </a>
                                            )}
                                            {row.PhoneNumber && (
                                                <div className={styles.contactPhone}>
                                                    <i className="fa-solid fa-phone" /> {row.PhoneNumber}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <table className={styles.table}>
                                    <thead>
                                    <tr>
                                        <th className={styles.th}>Контрагент</th>
                                        <th className={styles.th}>Должность</th>
                                        <th className={styles.th}>Фамилия и имя</th>
                                        <th className={styles.th}>Email</th>
                                        <th className={styles.th}>Телефон</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(data.Table4?.Values as Table4Value[])?.map((row, i) => (
                                        <tr key={i} className={styles.tr}>
                                            <td className={styles.td}>{row.ContractorName || '—'}</td>
                                            <td className={styles.td}>{row.Position || '—'}</td>
                                            <td className={styles.td}>{row.Title || '—'}</td>
                                            <td className={styles.td}>
                                                {row.Email
                                                    ? <a href={`mailto:${row.Email}`}
                                                         className={styles.emailLink}>{row.Email}</a>
                                                    : '—'}
                                            </td>
                                            <td className={`${styles.td} ${styles.tdNum}`}>{row.PhoneNumber || '—'}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}
                        </GreenTableCard>
                    )}
                </div>
            )}
        </div>
    );
};
